import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["state", "showOnHover"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useRef, useEffect } from 'react';
import { addGlobalEventListener } from 'ariakit-utils/events';
import { useBooleanEvent, useIsMouseMoving, useEvent, useForkRef } from 'ariakit-utils/hooks';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { useFocusable } from '../focusable/focusable.js';
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an anchor element that will open a popover
 * (`Hovercard`) on hover.
 * @see https://ariakit.org/components/hovercard
 * @example
 * ```jsx
 * const state = useHovercardState();
 * const props = useHovercardAnchor({ state });
 * <Role as="a" {...props}>@username</Role>
 * <Hovercard state={state}>Details</Hovercard>
 * ```
 */

var useHovercardAnchor = createHook(function (_ref) {
  var state = _ref.state,
      _ref$showOnHover = _ref.showOnHover,
      showOnHover = _ref$showOnHover === void 0 ? true : _ref$showOnHover,
      props = _objectWithoutProperties(_ref, _excluded);

  var disabled = props.disabled || props["aria-disabled"] === true || props["aria-disabled"] === "true";
  var showTimeoutRef = useRef(0); // Clear the show timeout if the anchor is unmounted

  useEffect(function () {
    return function () {
      return window.clearTimeout(showTimeoutRef.current);
    };
  }, []); // Clear the show timeout if the mouse leaves the anchor element. We're
  // using the native mouseleave event instead of React's onMouseLeave so we
  // bypass the event.stopPropagation() logic set on the Hovercard component
  // for when the mouse is moving toward the Hovercard.

  useEffect(function () {
    var onMouseLeave = function onMouseLeave(event) {
      var element = state.anchorRef.current;
      if (!element) return;
      if (event.target !== element) return;
      window.clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = 0;
    };

    return addGlobalEventListener("mouseleave", onMouseLeave, true);
  }, [state.anchorRef]);
  var onMouseMoveProp = props.onMouseMove;
  var showOnHoverProp = useBooleanEvent(showOnHover);
  var isMouseMoving = useIsMouseMoving();
  var onMouseMove = useEvent(function (event) {
    state.anchorRef.current = event.currentTarget;
    onMouseMoveProp == null ? void 0 : onMouseMoveProp(event);
    if (disabled) return;
    if (event.defaultPrevented) return;
    if (showTimeoutRef.current) return;
    if (!isMouseMoving()) return;
    if (!showOnHoverProp(event)) return;
    showTimeoutRef.current = window.setTimeout(function () {
      showTimeoutRef.current = 0;
      state.show();
    }, state.showTimeout);
  });
  props = _objectSpread(_objectSpread({}, props), {}, {
    ref: useForkRef(state.anchorRef, props.ref),
    onMouseMove: onMouseMove
  });
  props = useFocusable(props);
  return props;
});
/**
 * A component that renders an anchor element that will open a popover
 * (`Hovercard`) on hover.
 * @see https://ariakit.org/components/hovercard
 * @example
 * ```jsx
 * const hovercard = useHovercardState();
 * <HovercardAnchor state={hovercard}>@username</HovercardAnchor>
 * <Hovercard state={hovercard}>Details</Hovercard>
 * ```
 */

var HovercardAnchor = createComponent(function (props) {
  var htmlProps = useHovercardAnchor(props);
  return createElement("a", htmlProps);
});
export { HovercardAnchor, useHovercardAnchor };